import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Banknote, Plus, ArrowLeft, Sparkles, Leaf, Gauge, FileText, Target, FolderCheck,
  Calculator, Trash2, Check, Wand2, TrendingUp, Building2, ClipboardList, CheckCircle2,
  ShieldCheck, Landmark, LineChart, Send, HelpCircle,
} from "lucide-react";
import { formatSgd, formatNum } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { toast } from "sonner";
import CatalogTab from "../components/greenfinance/CatalogTab";
import MatchedOpportunitiesTab from "../components/greenfinance/MatchedOpportunitiesTab";
import FinancingCasesTab from "../components/greenfinance/FinancingCasesTab";
import BankSourcesTab from "../components/greenfinance/BankSourcesTab";
import GrantsTab from "../components/greenfinance/GrantsTab";
import SusfinOverview from "../components/susfin/SusfinOverview";
import InvestmentRegister from "../components/susfin/InvestmentRegister";
import FundingProfile from "../components/susfin/FundingProfile";
import MrvTab from "../components/susfin/MrvTab";
import CfoDashboard from "../components/susfin/CfoDashboard";
import TargetGapDashboard from "../components/susfin/TargetGapDashboard";
import CapitalTwin from "../components/susfin/CapitalTwin";
import FundingCalendar from "../components/susfin/FundingCalendar";
import FundingCopilotTab from "../components/susfin/CopilotTab";

const PURPOSES = [
  { value: "green_project", label: "Specific green CAPEX project" },
  { value: "corporate_kpi", label: "Corporate purpose (KPI-linked)" },
  { value: "transition", label: "Transition (hard-to-abate)" },
];
const KPI_MATURITY = [
  { value: "none", label: "No KPI tracking yet" },
  { value: "basic", label: "Basic KPI tracking" },
  { value: "mature", label: "Mature KPI tracking" },
];
const STATUS_VARIANT = { draft: "outline", ready: "success", submitted: "accent" };

function Toggle({ checked, onChange, label, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`flex items-center gap-2 px-3 py-2 rounded-sm border text-sm text-left transition-colors ${checked ? "border-accent bg-accent/10 text-foreground" : "border-border hover:bg-surface-hover text-muted-foreground"}`}>
      <span className={`h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 ${checked ? "bg-accent border-accent" : "border-border"}`}>
        {checked && <Check className="h-3 w-3 text-black" />}
      </span>
      {label}
    </button>
  );
}

const Stat = ({ label, value, sub, testid, pkey }) => (
  <div className="card-flat p-5 group" data-testid={testid}>
    <div className="label-eyebrow mb-1 flex items-center gap-1.5">{label}{pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}</div>
    <div className="font-display text-3xl font-bold tracking-tight">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function GreenFinancing() {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [reference, setReference] = useState({ schemes: [], categories: [], documents: [] });
  const [selected, setSelected] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [finTab, setFinTab] = useState("readiness");
  const [bankTab, setBankTab] = useState("catalog");
  const [openCaseId, setOpenCaseId] = useState(null);
  const [openMrvId, setOpenMrvId] = useState(null);
  const [dashTab, setDashTab] = useState("cfo");

  const schemeName = (code) => reference.schemes.find((s) => s.code === code)?.name || code;

  const load = async () => {
    const [d, c, r] = await Promise.all([
      api.get("/financing/dashboard"),
      api.get("/financing"),
      api.get("/financing/reference"),
    ]);
    setStats(d.data); setCases(c.data.cases); setReference(r.data);
  };
  useEffect(() => { load(); }, []);

  if (selected) {
    return <CaseDetail caseId={selected} reference={reference} schemeName={schemeName}
      onBack={() => { setSelected(null); load(); }} onChanged={load} />;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="green-financing-page">
      <div>
        <div className="label-eyebrow mb-1 flex items-center gap-2"><Leaf className="h-3.5 w-3.5 text-accent" /> Module · Green & Sustainable Financing</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          Green Financing
          <Link to="/financing/guide" title="Open the module user guide" data-testid="gf-guide-link"
            className="text-muted-foreground/50 hover:text-accent transition-colors">
            <HelpCircle className="h-4 w-4" />
          </Link>
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          From ESG intelligence to funded impact — maintain your funding profile, build a register of
          sustainability investments, match to Singapore grants and green financing, and manage
          applications and financing cases end to end.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto items-center gap-1" data-testid="gf-tablist">
          <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 pl-1 pr-0.5 select-none">Grants</span>
          <TabsTrigger value="grants" data-testid="gf-tab-grants">Grants</TabsTrigger>
          <TabsTrigger value="profile" data-testid="gf-tab-profile">Funding Profile</TabsTrigger>

          <span className="h-5 w-px bg-border mx-1.5 shrink-0" aria-hidden="true" />
          <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 px-0.5 select-none">Financing</span>
          <TabsTrigger value="financing" data-testid="gf-tab-financing">Bank Financing</TabsTrigger>

          <span className="h-5 w-px bg-border mx-1.5 shrink-0" aria-hidden="true" />
          <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 px-0.5 select-none">Shared</span>
          <TabsTrigger value="overview" data-testid="gf-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="copilot" data-testid="gf-tab-copilot">Copilot</TabsTrigger>
          <TabsTrigger value="register" data-testid="gf-tab-register">Investment Register</TabsTrigger>
          <TabsTrigger value="mrv" data-testid="gf-tab-mrv">MRV</TabsTrigger>
          <TabsTrigger value="dashboards" data-testid="gf-tab-dashboards">Dashboards</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="gf-tab-calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <SusfinOverview onNavigate={setTab} />
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <InvestmentRegister onNavigate={setTab}
            onOpenFinancingCase={(cid) => { setOpenCaseId(cid); setFinTab("cases"); setTab("financing"); }}
            onOpenMrv={(iid) => { setOpenMrvId(iid); setTab("mrv"); }} />
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          <FundingProfile />
        </TabsContent>

        <TabsContent value="grants" className="mt-4"><GrantsTab /></TabsContent>

        <TabsContent value="mrv" className="mt-4">
          <MrvTab openId={openMrvId} clearOpenId={() => setOpenMrvId(null)} />
        </TabsContent>

        <TabsContent value="dashboards" className="mt-4">
          <Tabs value={dashTab} onValueChange={setDashTab}>
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="cfo" data-testid="gf-tab-cfo">CFO Portfolio</TabsTrigger>
              <TabsTrigger value="gaps" data-testid="gf-tab-gaps">Target Funding Gap</TabsTrigger>
              <TabsTrigger value="twin" data-testid="gf-tab-twin">Capital Twin</TabsTrigger>
            </TabsList>
            <TabsContent value="cfo" className="mt-4"><CfoDashboard /></TabsContent>
            <TabsContent value="gaps" className="mt-4"><TargetGapDashboard /></TabsContent>
            <TabsContent value="twin" className="mt-4"><CapitalTwin /></TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <FundingCalendar onNavigate={setTab} />
        </TabsContent>

        <TabsContent value="copilot" className="mt-4">
          <FundingCopilotTab />
        </TabsContent>

        <TabsContent value="financing" className="mt-4">
          <Tabs value={finTab} onValueChange={setFinTab}>
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="marketplace" data-testid="gf-tab-marketplace">Marketplace</TabsTrigger>
              <TabsTrigger value="readiness" data-testid="gf-tab-readiness">Readiness Prep</TabsTrigger>
              <TabsTrigger value="matched" data-testid="gf-tab-matched">Matched Opportunities</TabsTrigger>
              <TabsTrigger value="cases" data-testid="gf-tab-cases">Financing Cases</TabsTrigger>
            </TabsList>

            <TabsContent value="marketplace" className="mt-4">
              <Tabs value={bankTab} onValueChange={setBankTab}>
                <TabsList className="flex flex-wrap h-auto">
                  <TabsTrigger value="catalog" data-testid="gf-tab-catalog">Catalog</TabsTrigger>
                  <TabsTrigger value="sources" data-testid="gf-tab-sources">Bank Sources</TabsTrigger>
                </TabsList>
                <TabsContent value="catalog" className="mt-4"><CatalogTab /></TabsContent>
                <TabsContent value="sources" className="mt-4"><BankSourcesTab /></TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="readiness" className="mt-4 space-y-6">
              <div className="flex justify-end">
                <Button variant="primary" onClick={() => setNavOpen(true)} data-testid="new-case-btn"><Plus className="h-4 w-4" /> New readiness case</Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Financing cases" value={stats?.total_cases ?? "—"} testid="stat-cases" pkey="gf.cases"
                  sub={cases.length ? `${cases.filter((c) => !(c.financing_quantum_requested > 0)).length} readiness prep · ${cases.filter((c) => c.financing_quantum_requested > 0).length} financing case${cases.filter((c) => c.financing_quantum_requested > 0).length === 1 ? "" : "s"}` : undefined} />
                <Stat label="Pipeline value" value={stats ? formatSgd(stats.total_amount_sgd) : "—"} testid="stat-amount" pkey="gf.pipeline" />
                <Stat label="Avg readiness" value={stats ? `${stats.avg_readiness}/100` : "—"} sub={`${stats?.ready_to_submit ?? 0} ready to submit`} testid="stat-readiness" pkey="gf.readiness" />
                <Stat label="CO₂ avoided / yr" value={stats ? `${formatNum(stats.total_co2_avoided_tonnes, { decimals: 1 })} t` : "—"} testid="stat-co2" pkey="gf.co2" />
              </div>

              <div className="space-y-3">
                {cases.map((c) => {
                  const r = c.readiness || {};
                  return (
                    <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`case-${c.id}`}
                      className="w-full text-left card-flat p-5 hover:bg-surface-hover transition-colors flex items-center gap-5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display font-semibold truncate">{c.name}</h3>
                          <Badge variant={STATUS_VARIANT[c.status] || "outline"} className="text-[10px] uppercase">{c.status}</Badge>
                          <Badge variant="outline" className="text-[10px]">{schemeName(c.recommendation?.primary_type)}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">
                          {reference.categories.find((x) => x.value === c.project?.category)?.label || c.project?.category || "—"}
                          {c.navigator?.amount_sgd ? ` · ${formatSgd(c.navigator.amount_sgd)}` : ""}
                          {c.simulation?.co2_avoided_tonnes ? ` · ${c.simulation.co2_avoided_tonnes} tCO₂e/yr` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0 w-32">
                        <div className="font-display text-2xl font-bold">{r.overall ?? 0}<span className="text-sm text-muted-foreground">/100</span></div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.rating || "Early"} readiness</div>
                      </div>
                    </button>
                  );
                })}
                {cases.length === 0 && (
                  <div className="card-flat p-12 text-center text-muted-foreground" data-testid="cases-empty">
                    <Banknote className="h-8 w-8 mx-auto mb-3 opacity-50" />
                    No readiness cases yet — start one with the Opportunity Navigator.
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="matched" className="mt-4">
              <MatchedOpportunitiesTab onOpenCase={(id) => { setOpenCaseId(id); setFinTab("cases"); }} />
            </TabsContent>
            <TabsContent value="cases" className="mt-4">
              <FinancingCasesTab openCaseId={openCaseId} clearOpenCase={() => setOpenCaseId(null)} />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <NavigatorDialog open={navOpen} onOpenChange={setNavOpen} reference={reference}
        onCreated={(id) => { setNavOpen(false); load(); setSelected(id); }} />
    </div>
  );
}

function NavigatorDialog({ open, onOpenChange, reference, onCreated }) {
  const blank = { name: "", entity_summary: "", project_category: "", financing_purpose: "green_project", amount_sgd: "", has_audited_financials: false, has_projections: false, kpi_maturity: "none", external_review_planned: false };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [insightBusy, setInsightBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF(blank);
    setInsightBusy(true);
    api.get("/financing/meta/company-insights")
      .then(({ data }) => setF((p) => ({ ...p, entity_summary: p.entity_summary || data.summary || "" })))
      .catch(() => {})
      .finally(() => setInsightBusy(false));
    /* eslint-disable-next-line */
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/financing", { ...f, amount_sgd: f.amount_sgd ? parseFloat(f.amount_sgd) : null });
      toast.success("Financing case created");
      onCreated(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create case");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="navigator-dialog">
        <DialogHeader><DialogTitle>Opportunity Navigator</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Answer a few questions — we'll recommend a financing route and applicable Singapore schemes.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Case name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Rooftop solar — HQ" data-testid="nav-name" /></div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Company summary (optional)</Label>
              {insightBusy
                ? <span className="text-[11px] text-accent flex items-center gap-1"><Sparkles className="h-3 w-3 animate-pulse" /> Auto-capturing insights…</span>
                : <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Sparkles className="h-3 w-3 text-accent" /> Auto-drafted from your company data · editable</span>}
            </div>
            <Textarea rows={3} value={f.entity_summary} onChange={(e) => set("entity_summary", e.target.value)} placeholder={insightBusy ? "Generating a summary from your company profile…" : "Manufacturing SME, 15 yrs operating, S$30M revenue…"} data-testid="nav-summary" />
          </div>
          <div>
            <Label>Project category</Label>
            <Select value={f.project_category} onValueChange={(v) => set("project_category", v)}>
              <SelectTrigger data-testid="nav-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{reference.categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Financing purpose</Label>
            <Select value={f.financing_purpose} onValueChange={(v) => set("financing_purpose", v)}>
              <SelectTrigger data-testid="nav-purpose"><SelectValue /></SelectTrigger>
              <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Indicative amount (S$)</Label><Input type="text" inputMode="numeric" value={f.amount_sgd} onChange={(e) => set("amount_sgd", e.target.value.replace(/[^0-9]/g, ""))} placeholder="500000" data-testid="nav-amount" /></div>
          <div>
            <Label>KPI maturity</Label>
            <Select value={f.kpi_maturity} onValueChange={(v) => set("kpi_maturity", v)}>
              <SelectTrigger data-testid="nav-kpi-maturity"><SelectValue /></SelectTrigger>
              <SelectContent>{KPI_MATURITY.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Toggle checked={f.has_audited_financials} onChange={(v) => set("has_audited_financials", v)} label="Audited financials available" testid="nav-audited" />
          <Toggle checked={f.has_projections} onChange={(v) => set("has_projections", v)} label="Cash-flow projections ready" testid="nav-projections" />
          <Toggle checked={f.external_review_planned} onChange={(v) => set("external_review_planned", v)} label="External review (SPO) planned" testid="nav-review" />
        </div>
        <Button variant="primary" onClick={submit} disabled={busy || !f.name || !f.project_category} className="w-full" data-testid="nav-submit">
          {busy ? "Analysing…" : "Get recommendation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CaseDetail({ caseId, reference, schemeName, onBack, onChanged }) {
  const [c, setC] = useState(null);
  const reload = async () => { const { data } = await api.get(`/financing/${caseId}`); setC(data); };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [caseId]);
  if (!c) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const setStatus = async (status) => {
    const { data } = await api.put(`/financing/${caseId}/status`, { status }); setC(data); onChanged?.();
    toast.success(`Marked ${status}`);
  };
  const remove = async () => {
    if (!window.confirm("Delete this financing case?")) return;
    await api.delete(`/financing/${caseId}`); onBack();
  };

  const rec = c.recommendation || {};
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="case-detail">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> All cases</button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-2xl font-bold tracking-tight">{c.name}</h1>
            <Badge variant={STATUS_VARIANT[c.status] || "outline"} className="uppercase text-[10px]">{c.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{rec.rationale}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="accent">{schemeName(rec.primary_type)}</Badge>
            {(rec.schemes || []).filter((s) => s.code !== rec.primary_type).map((s) => <Badge key={s.code} variant="outline" className="text-[10px]">{s.name}</Badge>)}
            <span className="text-xs text-muted-foreground">Confidence {Math.round((rec.confidence || 0) * 100)}%</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {c.status !== "ready" && <Button variant="outline" size="sm" onClick={() => setStatus("ready")} data-testid="mark-ready-btn">Mark ready</Button>}
          {c.status === "ready" && <Button variant="primary" size="sm" onClick={() => setStatus("submitted")} data-testid="mark-submitted-btn">Mark submitted</Button>}
          <Button variant="ghost" size="icon" onClick={remove} data-testid="delete-case-btn"><Trash2 className="h-4 w-4 text-red-400" /></Button>
        </div>
      </div>

      <Tabs defaultValue="readiness">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="readiness" data-testid="tab-readiness"><Gauge className="h-3.5 w-3.5 mr-1" /> Readiness</TabsTrigger>
          <TabsTrigger value="project" data-testid="tab-project"><Building2 className="h-3.5 w-3.5 mr-1" /> Project</TabsTrigger>
          <TabsTrigger value="simulation" data-testid="tab-simulation"><Calculator className="h-3.5 w-3.5 mr-1" /> Simulation</TabsTrigger>
          <TabsTrigger value="kpis" data-testid="tab-kpis"><Target className="h-3.5 w-3.5 mr-1" /> KPI Studio</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><ClipboardList className="h-3.5 w-3.5 mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="governance" data-testid="tab-governance"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Governance</TabsTrigger>
          <TabsTrigger value="facility" data-testid="tab-facility"><Landmark className="h-3.5 w-3.5 mr-1" /> Facility & Utilisation</TabsTrigger>
          <TabsTrigger value="monitoring" data-testid="tab-monitoring"><LineChart className="h-3.5 w-3.5 mr-1" /> Monitoring</TabsTrigger>
          <TabsTrigger value="copilot" data-testid="tab-copilot"><Sparkles className="h-3.5 w-3.5 mr-1" /> AI Co-Pilot</TabsTrigger>
          <TabsTrigger value="pack" data-testid="tab-pack"><FileText className="h-3.5 w-3.5 mr-1" /> Pack</TabsTrigger>
        </TabsList>

        <TabsContent value="readiness"><ReadinessTab caseId={caseId} c={c} setC={setC} onChanged={onChanged} /></TabsContent>
        <TabsContent value="project"><ProjectTab caseId={caseId} c={c} setC={setC} reference={reference} onChanged={onChanged} /></TabsContent>
        <TabsContent value="simulation"><SimulationTab caseId={caseId} c={c} setC={setC} onChanged={onChanged} /></TabsContent>
        <TabsContent value="kpis"><KpiTab caseId={caseId} c={c} setC={setC} onChanged={onChanged} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab caseId={caseId} c={c} setC={setC} onChanged={onChanged} /></TabsContent>
        <TabsContent value="governance"><GovernanceTab caseId={caseId} c={c} setC={setC} onChanged={onChanged} /></TabsContent>
        <TabsContent value="facility"><FacilityTab caseId={caseId} c={c} setC={setC} reference={reference} onChanged={onChanged} /></TabsContent>
        <TabsContent value="monitoring"><MonitoringTab caseId={caseId} c={c} setC={setC} onChanged={onChanged} /></TabsContent>
        <TabsContent value="copilot"><CopilotTab caseId={caseId} c={c} setC={setC} /></TabsContent>
        <TabsContent value="pack"><PackTab caseId={caseId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReadinessTab({ caseId, c, setC, onChanged }) {
  const [busy, setBusy] = useState(false);
  const r = c.readiness || {};
  const genPlan = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/financing/${caseId}/ai/action-plan`); setC({ ...c, action_plan: data.action_plan }); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4 mt-4">
      <div className="card-flat p-6 text-center" data-testid="readiness-overall">
        <div className="label-eyebrow mb-2">Overall readiness</div>
        <div className="font-display text-6xl font-bold">{r.overall ?? 0}</div>
        <div className="text-sm text-muted-foreground">out of 100</div>
        <Badge variant={r.overall >= 75 ? "success" : r.overall >= 50 ? "warning" : "outline"} className="mt-3">{r.rating} · ~{r.estimated_weeks} wks to ready</Badge>
      </div>
      <div className="card-flat p-6 lg:col-span-2 space-y-3">
        <div className="label-eyebrow">Readiness dimensions</div>
        {(r.dimensions || []).map((d) => (
          <div key={d.key} data-testid={`dim-${d.key}`}>
            <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{d.label}</span><span className="font-mono">{d.score}</span></div>
            <Progress value={d.score} />
          </div>
        ))}
      </div>
      <div className="card-flat p-6 lg:col-span-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Gap analysis & action plan</div>
          <Button variant="outline" size="sm" onClick={genPlan} disabled={busy} data-testid="gen-action-plan-btn"><Wand2 className="h-3.5 w-3.5" /> {busy ? "Thinking…" : "AI action plan"}</Button>
        </div>
        {(r.gaps || []).length > 0 ? (
          <ul className="space-y-1.5">{r.gaps.map((g, i) => <li key={i} className="text-sm flex gap-2"><span className="text-amber-400">●</span><span className="text-muted-foreground">{g}</span></li>)}</ul>
        ) : <div className="text-sm text-emerald-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> No major gaps — proceed to the application pack.</div>}
        {c.action_plan && <div className="card-flat bg-surface p-4 text-sm whitespace-pre-wrap mt-2" data-testid="action-plan-text">{c.action_plan}</div>}
      </div>
    </div>
  );
}

const NUM_FIELDS = [
  ["capex_sgd", "CAPEX (S$)"], ["opex_sgd", "Annual OPEX (S$)"], ["annual_savings_sgd", "Annual savings (S$, optional)"],
  ["energy_savings_kwh", "Energy savings (kWh/yr)"], ["fuel_savings_litres", "Fuel savings (L/yr)"],
  ["water_savings_m3", "Water savings (m³/yr)"], ["tariff_sgd_per_kwh", "Electricity tariff (S$/kWh)"], ["tenure_years", "Financing tenure (yrs)"],
];

function ProjectTab({ caseId, c, setC, reference, onChanged }) {
  const [f, setF] = useState({ ...(c.project || {}) });
  const [busy, setBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const draftBenefit = async () => {
    setDraftBusy(true);
    try { const { data } = await api.post(`/financing/${caseId}/ai/benefit`); set("expected_benefit", data.text); }
    catch (e) { toast.error("Could not draft"); } finally { setDraftBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = {}; Object.entries(f).forEach(([k, v]) => { payload[k] = NUM_FIELDS.some(n => n[0] === k) && v !== "" && v != null ? parseFloat(v) : v; });
      const { data } = await api.put(`/financing/${caseId}/project`, payload);
      setC(data); onChanged?.(); toast.success("Project saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-6 mt-4 space-y-4" data-testid="project-tab">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label>Project title</Label><Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="proj-title" /></div>
        <div>
          <Label>Category</Label>
          <Select value={f.category || ""} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="proj-category"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{reference.categories.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Location</Label><Input value={f.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="proj-location" /></div>
        <div><Label>Project owner</Label><Input value={f.owner || ""} onChange={(e) => set("owner", e.target.value)} data-testid="proj-owner" /></div>
        <div><Label>Vendor</Label><Input value={f.vendor || ""} onChange={(e) => set("vendor", e.target.value)} data-testid="proj-vendor" /></div>
        <div><Label>Start date</Label><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="proj-start" /></div>
        <div><Label>End date</Label><Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="proj-end" /></div>
        {NUM_FIELDS.map(([k, lbl]) => (
          <div key={k}><Label>{lbl}</Label><Input type="number" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`proj-${k}`} /></div>
        ))}
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <Label>Expected environmental benefit</Label>
            <Button variant="outline" size="sm" onClick={draftBenefit} disabled={draftBusy} data-testid="draft-benefit-btn"><Sparkles className="h-3.5 w-3.5" /> {draftBusy ? "Drafting…" : "AI draft"}</Button>
          </div>
          <Textarea rows={2} value={f.expected_benefit || ""} onChange={(e) => set("expected_benefit", e.target.value)} placeholder="Describe the environmental benefit, or click AI draft to generate from the category & simulation." data-testid="proj-benefit" />
        </div>
      </div>
      <Button variant="primary" onClick={save} disabled={busy} data-testid="save-project-btn">{busy ? "Saving…" : "Save project"}</Button>
    </div>
  );
}

function SimulationTab({ caseId, c, setC, onChanged }) {
  const [busy, setBusy] = useState(false);
  const sim = c.simulation || {};
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/financing/${caseId}/simulate`); setC(data.case); onChanged?.(); toast.success("Simulation updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const has = Object.keys(sim).length > 0;
  const cards = has ? [
    ["Annual savings", formatSgd(sim.annual_savings_sgd)], ["Payback", `${sim.payback_years ?? "—"} yrs`],
    ["ROI", `${sim.roi_pct ?? "—"}%`], ["IRR (proxy)", `${sim.irr_pct ?? "—"}%`],
    ["Indicative loan", formatSgd(sim.indicative_loan_sgd)], ["DSCR support", sim.dscr ?? "—"],
    ["CO₂ avoided / yr", `${sim.co2_avoided_tonnes ?? "—"} tCO₂e`], ["Lifetime CO₂", `${sim.co2_avoided_lifetime_tonnes ?? "—"} tCO₂e`],
  ] : [];
  return (
    <div className="space-y-4 mt-4" data-testid="simulation-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Deterministic business case from your project inputs (CAPEX, savings, SG grid factor 0.4168 kgCO₂/kWh).</p>
        <Button variant="primary" size="sm" onClick={run} disabled={busy} data-testid="run-simulation-btn"><TrendingUp className="h-3.5 w-3.5" /> {busy ? "Running…" : "Run simulation"}</Button>
      </div>
      {has ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map(([l, v]) => <div key={l} className="card-flat p-4" data-testid={`sim-${l}`}><div className="label-eyebrow mb-1">{l}</div><div className="font-display text-xl font-bold">{v}</div></div>)}
          </div>
          <div className="card-flat p-5">
            <div className="label-eyebrow mb-3">Scenario analysis</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {(sim.scenarios || []).map((s) => (
                <div key={s.name} className="card-flat bg-surface p-4" data-testid={`scenario-${s.name}`}>
                  <Badge variant={s.name === "base" ? "accent" : "outline"} className="uppercase text-[10px] mb-2">{s.name}</Badge>
                  <div className="text-sm">Savings: <span className="font-mono">{formatSgd(s.annual_savings_sgd)}</span>/yr</div>
                  <div className="text-sm">Payback: <span className="font-mono">{s.payback_years ?? "—"} yrs</span></div>
                  <div className="text-sm">Net benefit: <span className="font-mono">{formatSgd(s.net_benefit_sgd)}</span></div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sim-empty"><Calculator className="h-8 w-8 mx-auto mb-3 opacity-50" />Add CAPEX & savings in the Project tab, then run the simulation.</div>}
    </div>
  );
}

function KpiTab({ caseId, c, setC, onChanged }) {
  const [recommended, setRecommended] = useState([]);
  const [selected, setSelected] = useState(c.kpis || []);
  const [busy, setBusy] = useState(false);
  const recommend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/financing/${caseId}/ai/kpis`); setRecommended(data.recommended); }
    finally { setBusy(false); }
  };
  const add = (k) => { if (!selected.some((x) => x.name === k.name)) setSelected([...selected, k]); };
  const removeAt = (i) => setSelected(selected.filter((_, idx) => idx !== i));
  const save = async () => {
    const { data } = await api.post(`/financing/${caseId}/kpis`, { kpis: selected.map((k) => ({ name: k.name, unit: k.unit || "", baseline: k.baseline || "", target: String(k.target || ""), target_year: String(k.target_year || ""), methodology: k.methodology || "" })) });
    setC(data); onChanged?.(); toast.success("KPIs saved");
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-4" data-testid="kpi-tab">
      <div className="card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">AI-recommended KPIs / SPTs</div>
          <Button variant="outline" size="sm" onClick={recommend} disabled={busy} data-testid="recommend-kpis-btn"><Sparkles className="h-3.5 w-3.5" /> {busy ? "Designing…" : "Recommend"}</Button>
        </div>
        {recommended.length === 0 && <div className="text-sm text-muted-foreground">Generate KPI candidates tailored to your borrower profile and financing route.</div>}
        {recommended.map((k, i) => (
          <div key={i} className="card-flat bg-surface p-3" data-testid={`rec-kpi-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-sm">{k.name}</div>
              <Button variant="ghost" size="sm" onClick={() => add(k)} data-testid={`add-kpi-${i}`}><Plus className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Target {k.target}{k.unit} by {k.target_year} · {k.methodology}</div>
            {k.suitability && <Badge variant="outline" className="text-[10px] mt-2">{k.suitability}</Badge>}
          </div>
        ))}
      </div>
      <div className="card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Selected KPI framework</div>
          <Button variant="primary" size="sm" onClick={save} data-testid="save-kpis-btn">Save</Button>
        </div>
        {selected.length === 0 && <div className="text-sm text-muted-foreground">Add KPIs from the recommendations (for SLL / transition routes).</div>}
        {selected.map((k, i) => (
          <div key={i} className="card-flat bg-surface p-3" data-testid={`sel-kpi-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-sm">{k.name}</div>
              <Button variant="ghost" size="icon" onClick={() => removeAt(i)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Input className="text-xs" placeholder="Target" value={k.target || ""} onChange={(e) => setSelected(selected.map((x, idx) => idx === i ? { ...x, target: e.target.value } : x))} />
              <Input className="text-xs" placeholder="Unit" value={k.unit || ""} onChange={(e) => setSelected(selected.map((x, idx) => idx === i ? { ...x, unit: e.target.value } : x))} />
              <Input className="text-xs" placeholder="Year" value={k.target_year || ""} onChange={(e) => setSelected(selected.map((x, idx) => idx === i ? { ...x, target_year: e.target.value } : x))} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DOC_STATUS = [{ v: "missing", l: "Missing" }, { v: "in_progress", l: "In progress" }, { v: "ready", l: "Ready" }];
function DocumentsTab({ caseId, c, setC, onChanged }) {
  const update = async (key, status) => {
    const { data } = await api.put(`/financing/${caseId}/documents`, { key, status }); setC(data); onChanged?.();
  };
  const docs = c.documents || [];
  const ready = docs.filter((d) => d.status === "ready").length;
  return (
    <div className="card-flat p-6 mt-4 space-y-2" data-testid="documents-tab">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">Document checklist</div>
        <Badge variant={ready === docs.length ? "success" : "outline"}>{ready}/{docs.length} ready</Badge>
      </div>
      {docs.map((d) => (
        <div key={d.key} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0" data-testid={`doc-${d.key}`}>
          <div className="flex items-center gap-2 text-sm">
            <FolderCheck className={`h-4 w-4 ${d.status === "ready" ? "text-emerald-400" : "text-muted-foreground"}`} />
            {d.label}
          </div>
          <Select value={d.status} onValueChange={(v) => update(d.key, v)}>
            <SelectTrigger className="w-36" data-testid={`doc-status-${d.key}`}><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

const NARRATIVES = [
  { kind: "use_of_proceeds", label: "Use of proceeds" },
  { kind: "rationale", label: "Financing rationale" },
  { kind: "exec_summary", label: "Executive summary" },
];
function CopilotTab({ caseId, c, setC }) {
  const [busy, setBusy] = useState(null);
  const gen = async (kind) => {
    setBusy(kind);
    try { const { data } = await api.post(`/financing/${caseId}/ai/narrative`, { kind }); setC({ ...c, narratives: { ...(c.narratives || {}), [kind]: data.text } }); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-4 mt-4" data-testid="copilot-tab">
      <p className="text-sm text-muted-foreground">AI drafts grounded in this case's data. Review and edit before use — final outputs require human approval.</p>
      {NARRATIVES.map((n) => (
        <div key={n.kind} className="card-flat p-5" data-testid={`narrative-${n.kind}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="label-eyebrow">{n.label}</div>
            <Button variant="outline" size="sm" onClick={() => gen(n.kind)} disabled={busy === n.kind} data-testid={`gen-${n.kind}-btn`}><Sparkles className="h-3.5 w-3.5" /> {busy === n.kind ? "Drafting…" : "Draft"}</Button>
          </div>
          {(c.narratives || {})[n.kind]
            ? <div className="text-sm whitespace-pre-wrap text-foreground/90" data-testid={`narrative-text-${n.kind}`}>{c.narratives[n.kind]}</div>
            : <div className="text-sm text-muted-foreground">Not generated yet.</div>}
        </div>
      ))}
    </div>
  );
}

function PackTab({ caseId }) {
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const build = async () => { setBusy(true); try { const { data } = await api.get(`/financing/${caseId}/pack`); setPack(data); } finally { setBusy(false); } };
  useEffect(() => { build(); /* eslint-disable-next-line */ }, [caseId]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/financing/${caseId}/pack.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(pack?.case?.name || "financing").replace(/[^a-zA-Z0-9_-]/g, "_")}_financing_pack.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not export PDF");
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="pack-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">Assembled application pack — every section drawn from your case data.</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={build} disabled={busy} data-testid="rebuild-pack-btn">{busy ? "Building…" : "Rebuild"}</Button>
          <Button variant="primary" size="sm" onClick={exportPdf} disabled={exporting || !pack} data-testid="export-pdf-btn"><FileText className="h-3.5 w-3.5" /> {exporting ? "Exporting…" : "Export PDF"}</Button>
        </div>
      </div>
      {pack && (
        <>
          <div className="card-flat p-4 flex items-center justify-between" data-testid="pack-status">
            <div className="text-sm">Readiness <span className="font-mono">{pack.readiness?.overall}/100</span> · {pack.missing_documents.length} document(s) outstanding</div>
            <Badge variant={pack.complete ? "success" : "warning"}>{pack.complete ? "Lender-ready" : "Incomplete"}</Badge>
          </div>
          {pack.missing_documents.length > 0 && (
            <div className="card-flat bg-surface p-4 text-sm" data-testid="pack-missing">
              <div className="label-eyebrow mb-2">Outstanding documents</div>
              <ul className="list-disc list-inside text-muted-foreground space-y-0.5">{pack.missing_documents.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}
          {pack.sections.map((s, i) => (
            <div key={i} className="card-flat p-5" data-testid={`pack-section-${i}`}>
              <h4 className="font-display font-semibold mb-2">{i + 1}. {s.title}</h4>
              {s.body && <p className="text-sm text-foreground/85 whitespace-pre-wrap">{s.body}</p>}
              {s.fields && Object.keys(s.fields).length > 0 && (
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                  {Object.entries(s.fields).map(([k, v]) => <div key={k} className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">{k}</span><span className="font-mono text-right">{v ?? "—"}</span></div>)}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}


const DECISION_VARIANT = { approved: "success", rejected: "danger", pending: "outline" };
function GovernanceTab({ caseId, c, setC, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState({});
  const ap = c.approvals;
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/financing/${caseId}/approvals/submit`, {}); setC(data); onChanged?.(); toast.success("Submitted for internal approval"); }
    finally { setBusy(false); }
  };
  const decide = async (step_key, decision) => {
    const { data } = await api.post(`/financing/${caseId}/approvals/decision`, { step_key, decision, comment: comment[step_key] || "" });
    setC(data); onChanged?.(); toast.success(`${decision === "approved" ? "Approved" : "Rejected"}`);
  };
  return (
    <div className="card-flat p-6 mt-4 space-y-4" data-testid="governance-tab">
      {!ap?.submitted ? (
        <div className="text-center py-8">
          <ShieldCheck className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-4">Route this case through internal sign-offs (sponsor → sustainability → finance → CFO → board) before approaching lenders.</p>
          <Button variant="primary" onClick={submit} disabled={busy} data-testid="submit-approval-btn"><Send className="h-3.5 w-3.5" /> {busy ? "Submitting…" : "Submit for internal approval"}</Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Approval workflow</div>
            <Badge variant={DECISION_VARIANT[ap.decision]} className="uppercase" data-testid="approval-decision">{ap.decision}</Badge>
          </div>
          {ap.steps.map((s) => (
            <div key={s.key} className="card-flat bg-surface p-4" data-testid={`approval-${s.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground">{s.role}{s.approver ? ` · ${s.approver}` : ""}{s.comment ? ` — “${s.comment}”` : ""}</div>
                </div>
                <Badge variant={DECISION_VARIANT[s.status]} className="uppercase text-[10px]">{s.status}</Badge>
              </div>
              {s.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <Input className="text-xs" placeholder="Comment (optional)" value={comment[s.key] || ""} onChange={(e) => setComment({ ...comment, [s.key]: e.target.value })} data-testid={`approval-comment-${s.key}`} />
                  <Button variant="outline" size="sm" onClick={() => decide(s.key, "approved")} data-testid={`approve-${s.key}-btn`}><Check className="h-3.5 w-3.5" /> Approve</Button>
                  <Button variant="ghost" size="sm" onClick={() => decide(s.key, "rejected")} data-testid={`reject-${s.key}-btn`}>Reject</Button>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function FacilityTab({ caseId, c, setC, reference, onChanged }) {
  const [f, setF] = useState({ ...(c.facility || {}) });
  const [targets, setTargets] = useState([]);
  const [linked, setLinked] = useState(c.linked_target_id || "none");
  const [u, setU] = useState({ amount_sgd: "", date: "", project_line: "", vendor: "", category: "", invoice_ref: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/financing/meta/targets").then((r) => setTargets(r.data.targets)).catch(() => {}); }, []);

  const saveFacility = async () => {
    setBusy(true);
    try {
      const payload = { ...f, amount_sgd: f.amount_sgd ? parseFloat(f.amount_sgd) : null, tenure_years: f.tenure_years ? parseInt(f.tenure_years) : null, margin_bps: f.margin_bps ? parseFloat(f.margin_bps) : null };
      const { data } = await api.put(`/financing/${caseId}/facility`, payload); setC(data); onChanged?.(); toast.success("Facility secured");
    } finally { setBusy(false); }
  };
  const saveTarget = async (v) => {
    setLinked(v);
    const { data } = await api.put(`/financing/${caseId}/link-target`, { target_id: v === "none" ? null : v });
    setC(data); onChanged?.(); toast.success("Target link updated");
  };
  const addUtil = async () => {
    if (!u.amount_sgd) return;
    const { data } = await api.post(`/financing/${caseId}/utilisation`, { ...u, amount_sgd: parseFloat(u.amount_sgd) });
    setC(data); onChanged?.(); setU({ amount_sgd: "", date: "", project_line: "", vendor: "", category: "", invoice_ref: "" });
  };
  const delUtil = async (id) => { const { data } = await api.delete(`/financing/${caseId}/utilisation/${id}`); setC(data); onChanged?.(); };

  const util = c.utilisation || [];
  const drawn = util.reduce((s, r) => s + (r.amount_sgd || 0), 0);
  const facAmt = c.facility?.amount_sgd || 0;
  const pctDrawn = facAmt ? Math.round(drawn / facAmt * 100) : 0;

  return (
    <div className="space-y-4 mt-4" data-testid="facility-tab">
      <div className="card-flat p-5 border-accent/40 bg-accent/5">
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-accent" /> Link to decarbonisation target</div>
        <p className="text-xs text-muted-foreground mb-3">Once a facility is secured, this project's CO₂ avoided automatically feeds the linked target's progress bar on the Control Tower.</p>
        <Select value={linked} onValueChange={saveTarget}>
          <SelectTrigger className="max-w-md" data-testid="link-target-select"><SelectValue placeholder="Select a target" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No target linked</SelectItem>
            {targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Facility terms</div>
          {c.facility?.secured && <Badge variant="success" data-testid="facility-secured">Secured</Badge>}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Facility ID / ref</Label><Input value={f.facility_id || ""} onChange={(e) => set("facility_id", e.target.value)} data-testid="fac-id" /></div>
          <div><Label>Lender</Label><Input value={f.lender || ""} onChange={(e) => set("lender", e.target.value)} data-testid="fac-lender" /></div>
          <div><Label>Facility amount (S$)</Label><Input type="number" value={f.amount_sgd || ""} onChange={(e) => set("amount_sgd", e.target.value)} data-testid="fac-amount" /></div>
          <div><Label>Signed date</Label><Input type="date" value={f.signed_date || ""} onChange={(e) => set("signed_date", e.target.value)} data-testid="fac-date" /></div>
          <div><Label>Tenure (yrs)</Label><Input type="number" value={f.tenure_years || ""} onChange={(e) => set("tenure_years", e.target.value)} data-testid="fac-tenure" /></div>
          <div><Label>Margin (bps)</Label><Input type="number" value={f.margin_bps || ""} onChange={(e) => set("margin_bps", e.target.value)} data-testid="fac-margin" /></div>
        </div>
        <Button variant="primary" onClick={saveFacility} disabled={busy} data-testid="save-facility-btn">{busy ? "Saving…" : (c.facility?.secured ? "Update facility" : "Record secured facility")}</Button>
      </div>

      <div className="card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Funds utilisation ledger</div>
          <Badge variant="outline" data-testid="util-drawn">{formatSgd(drawn)} drawn{facAmt ? ` · ${pctDrawn}%` : ""}</Badge>
        </div>
        {facAmt > 0 && <Progress value={pctDrawn} />}
        <div className="grid sm:grid-cols-6 gap-2 items-end">
          <div className="sm:col-span-2"><Label>Amount (S$)</Label><Input type="number" value={u.amount_sgd} onChange={(e) => setU({ ...u, amount_sgd: e.target.value })} data-testid="util-amount" /></div>
          <div><Label>Date</Label><Input type="date" value={u.date} onChange={(e) => setU({ ...u, date: e.target.value })} data-testid="util-date" /></div>
          <div><Label>Project line</Label><Input value={u.project_line} onChange={(e) => setU({ ...u, project_line: e.target.value })} data-testid="util-line" /></div>
          <div><Label>Vendor</Label><Input value={u.vendor} onChange={(e) => setU({ ...u, vendor: e.target.value })} data-testid="util-vendor" /></div>
          <Button variant="outline" onClick={addUtil} data-testid="add-util-btn"><Plus className="h-4 w-4" /> Add</Button>
        </div>
        {util.map((r) => (
          <div key={r.id} className="flex items-center justify-between text-sm border-b border-border py-2" data-testid={`util-${r.id}`}>
            <div className="flex-1"><span className="font-mono">{formatSgd(r.amount_sgd)}</span> <span className="text-muted-foreground">· {r.date || "—"} · {r.project_line || "—"} {r.vendor ? `· ${r.vendor}` : ""}</span></div>
            <Button variant="ghost" size="icon" onClick={() => delUtil(r.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
          </div>
        ))}
        {util.length === 0 && <div className="text-sm text-muted-foreground">No drawdowns logged yet.</div>}
      </div>
    </div>
  );
}

const COV_STATUS = [{ v: "on_track", l: "On track" }, { v: "at_risk", l: "At risk" }, { v: "breached", l: "Breached" }, { v: "met", l: "Met" }];
function MonitoringTab({ caseId, c, setC, onChanged }) {
  const [rep, setRep] = useState({ period: "", kind: "use_of_proceeds", co2_avoided: "", savings_sgd: "", notes: "" });
  const [cov, setCov] = useState({ name: "", due_date: "", status: "on_track" });
  const reports = (c.monitoring || {}).reports || [];
  const covenants = (c.monitoring || {}).covenants || [];
  const addReport = async () => {
    if (!rep.period) return;
    const { data } = await api.post(`/financing/${caseId}/monitoring/report`, { ...rep, co2_avoided: rep.co2_avoided ? parseFloat(rep.co2_avoided) : null, savings_sgd: rep.savings_sgd ? parseFloat(rep.savings_sgd) : null });
    setC(data); onChanged?.(); setRep({ period: "", kind: "use_of_proceeds", co2_avoided: "", savings_sgd: "", notes: "" }); toast.success("Report added");
  };
  const addCov = async () => {
    if (!cov.name) return;
    const { data } = await api.post(`/financing/${caseId}/monitoring/covenant`, cov);
    setC(data); onChanged?.(); setCov({ name: "", due_date: "", status: "on_track" }); toast.success("Covenant added");
  };
  const setCovStatus = async (id, status) => { const { data } = await api.put(`/financing/${caseId}/monitoring/covenant/${id}`, { status }); setC(data); onChanged?.(); };
  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-4" data-testid="monitoring-tab">
      <div className="card-flat p-5 space-y-3">
        <div className="label-eyebrow">Periodic reports</div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Period (e.g. FY2025 H1)" value={rep.period} onChange={(e) => setRep({ ...rep, period: e.target.value })} data-testid="rep-period" />
          <Select value={rep.kind} onValueChange={(v) => setRep({ ...rep, kind: v })}>
            <SelectTrigger data-testid="rep-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="use_of_proceeds">Use of proceeds</SelectItem>
              <SelectItem value="sll_kpi">SLL KPI report</SelectItem>
              <SelectItem value="impact">Impact report</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" placeholder="CO₂ avoided (t)" value={rep.co2_avoided} onChange={(e) => setRep({ ...rep, co2_avoided: e.target.value })} data-testid="rep-co2" />
          <Input type="number" placeholder="Savings (S$)" value={rep.savings_sgd} onChange={(e) => setRep({ ...rep, savings_sgd: e.target.value })} data-testid="rep-savings" />
        </div>
        <Textarea rows={2} placeholder="Notes" value={rep.notes} onChange={(e) => setRep({ ...rep, notes: e.target.value })} />
        <Button variant="outline" size="sm" onClick={addReport} data-testid="add-report-btn"><Plus className="h-3.5 w-3.5" /> Add report</Button>
        {reports.map((r) => (
          <div key={r.id} className="card-flat bg-surface p-3 text-sm" data-testid={`report-${r.id}`}>
            <div className="flex items-center justify-between"><span className="font-medium">{r.period}</span><Badge variant="outline" className="text-[10px]">{r.kind.replace(/_/g, " ")}</Badge></div>
            <div className="text-xs text-muted-foreground mt-1">{r.co2_avoided ? `${r.co2_avoided} tCO₂e · ` : ""}{r.savings_sgd ? `${formatSgd(r.savings_sgd)} · ` : ""}{r.notes}</div>
          </div>
        ))}
        {reports.length === 0 && <div className="text-sm text-muted-foreground">No reports submitted yet.</div>}
      </div>
      <div className="card-flat p-5 space-y-3">
        <div className="label-eyebrow">Covenants & milestones</div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Covenant / milestone" value={cov.name} onChange={(e) => setCov({ ...cov, name: e.target.value })} data-testid="cov-name" />
          <Input type="date" value={cov.due_date} onChange={(e) => setCov({ ...cov, due_date: e.target.value })} data-testid="cov-due" />
        </div>
        <Button variant="outline" size="sm" onClick={addCov} data-testid="add-cov-btn"><Plus className="h-3.5 w-3.5" /> Add covenant</Button>
        {covenants.map((cv) => (
          <div key={cv.id} className="flex items-center justify-between gap-2 text-sm border-b border-border py-2" data-testid={`cov-${cv.id}`}>
            <div><div className="font-medium">{cv.name}</div><div className="text-xs text-muted-foreground">{cv.due_date || "—"}</div></div>
            <Select value={cv.status} onValueChange={(v) => setCovStatus(cv.id, v)}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{COV_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        {covenants.length === 0 && <div className="text-sm text-muted-foreground">No covenants tracked.</div>}
      </div>
    </div>
  );
}
